import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';
import { Save, ArrowLeft } from 'lucide-react';

const PropertyTypeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [usageCount, setUsageCount] = useState(0);

  const { control, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      code: ''
    }
  });

  useEffect(() => {
    if (isEditing) {
      fetchPropertyType();
      fetchUsageCount();
    }
  }, [isEditing, id]);

  const fetchPropertyType = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const { data: propertyType, error } = await supabase
        .from('property_types')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      if (propertyType) {
        // Set form values
        setValue('name', propertyType.name);
        setValue('code', propertyType.code || '');
      }
    } catch (error) {
      console.error('Error fetching property type:', error);
      toast.error('Errore nel caricamento della tipologia immobiliare');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageCount = async () => {
    if (!id) return;
    
    try {
      const { count, error } = await supabase
        .from('properties')
        .select('*', { count: 'exact', head: true })
        .eq('type_id', id);
      
      if (error) throw error;
      
      setUsageCount(count || 0);
    } catch (error) {
      console.error('Error fetching usage count:', error);
    }
  };

  const onSubmit = async (formData: any) => {
    setSubmitLoading(true);
    
    try {
      if (isEditing) {
        const { error } = await supabase
          .from('property_types')
          .update(formData)
          .eq('id', id);
        
        if (error) throw error;
        
        toast.success('Tipologia immobiliare aggiornata con successo');
      } else {
        const { error } = await supabase
          .from('property_types')
          .insert(formData);
        
        if (error) throw error;
        
        toast.success('Tipologia immobiliare creata con successo');
      }
      
      navigate('/admin/property-types');
    } catch (error: any) {
      console.error('Error saving property type:', error);
      toast.error(`Errore: ${error.message || 'Si è verificato un errore durante il salvataggio'}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title={isEditing ? "Modifica Tipologia Immobiliare" : "Nuova Tipologia Immobiliare"}>
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Caricamento...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditing ? "Modifica Tipologia Immobiliare" : "Nuova Tipologia Immobiliare"}>
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/property-types')}
          className="text-gray-600 hover:text-gray-900 flex items-center"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Torna alle tipologie immobiliari
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 pb-2 border-b">Informazioni Tipologia</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome*
              </label>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Il nome è obbligatorio' }}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    className={`w-full border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:ring-primary focus:border-primary`}
                    placeholder="Es. Appartamento, Villa, etc."
                  />
                )}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Codice
              </label>
              <Controller
                name="code"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    className={`w-full border ${errors.code ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:ring-primary focus:border-primary`}
                    placeholder="Es. APT, VIL, etc."
                  />
                )}
              />
              <p className="text-xs text-gray-500 mt-1">Codice identificativo opzionale per integrazioni esterne</p>
            </div>
          </div>

          {isEditing && usageCount > 0 && (
            <div className="mt-6 bg-blue-50 p-4 rounded-lg">
              <p className="text-blue-700">
                <strong>Nota:</strong> Questa tipologia immobiliare è attualmente utilizzata in {usageCount} immobili.
                La modifica influenzerà tutti gli immobili associati.
              </p>
            </div>
          )}
        </div>
        
        {/* Form Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/property-types')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={submitLoading}
            className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-light transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitLoading ? (
              <>
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Salvataggio...
              </>
            ) : (
              <>
                <Save className="h-5 w-5 mr-2" />
                Salva Tipologia
              </>
            )}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
};

export default PropertyTypeForm;